var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { createDiagramGroup, fitDiagramToBounds, readCanvasSize, setDiagramState, } from '../helpers';
import { createOrgNode, countOrgDescendants, resolveOrgBranchStyle, buildDistinctOrgBranchPalette, hashOrgBranchSeed, updateOrgCollapseButton, } from '../primitives';
import { layoutDiagram } from '../layouts';
import { wireOrgChartConnectors } from '../connectors';
/** Create org chart with optional collapse */
export function createOrgChart(app, root, options) {
    if (options === void 0) { options = {}; }
    var group = createDiagramGroup(app, 'orgChart', __assign(__assign({}, options), { root: root }), { name: 'orgChart' });
    var canvas = readCanvasSize(options);
    var levelGap = Math.max(120, Math.round(canvas.height * 0.22));
    var siblingGap = Math.max(28, Math.round(canvas.width * 0.04));
    group.metadata.orgLayout = { levelGap: levelGap, siblingGap: siblingGap };
    var rootNode = buildOrgNode(app, group, root, 0, 0, 0);
    layoutDiagram(rootNode, levelGap, siblingGap);
    wireOrgChartConnectors(app, group);
    wireOrgCollapseControls(app, group);
    return group;
}
/** Total nodes under this org data node (all descendants). */
function countOrgDataDescendants(data) {
    var e_1, _a;
    var _b;
    if (!((_b = data.children) === null || _b === void 0 ? void 0 : _b.length))
        return 0;
    var total = 0;
    try {
        for (var _c = __values(data.children), _d = _c.next(); !_d.done; _d = _c.next()) {
            var child = _d.value;
            total += 1 + countOrgDataDescendants(child);
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return total;
}
function buildOrgNode(app, parent, data, x, y, depth, branchIndex, branchPalette) {
    var _a, _b, _c, _d;
    if (branchIndex === void 0) { branchIndex = null; }
    if (branchPalette === void 0) { branchPalette = null; }
    var childCount = (_b = (_a = data.children) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
    var descendantCount = countOrgDataDescendants(data);
    var collapsed = (_c = data.collapsed) !== null && _c !== void 0 ? _c : false;
    var style = resolveOrgBranchStyle(depth, branchIndex, branchPalette);
    var _e = createOrgNode(app, {
        name: data.name,
        role: data.role,
        image: data.image,
        department: data.department,
        // Button uses total subtree size, not just direct children
        childCount: descendantCount,
        collapsed: collapsed,
        depth: depth,
        branchStyle: style,
    }), node = _e.node, indicator = _e.indicator;
    node.metadata.diagramId = data.name;
    node.metadata.orgName = data.name;
    node.x = x;
    node.y = y;
    node.metadata = __assign(__assign({}, node.metadata), { orgNode: true, collapsed: collapsed, childCount: childCount, descendantCount: descendantCount, orgBranchIndex: branchIndex, orgChildrenData: (_d = data.children) !== null && _d !== void 0 ? _d : [] });
    if (indicator) {
        node.metadata.collapseIndicator = indicator;
    }
    if (data.children && data.children.length > 0) {
        // Root: allocate unique colors for every top-level branch (N, no repeats)
        var palette_1 = depth === 0
            ? buildDistinctOrgBranchPalette(data.children.length, hashOrgBranchSeed(data.children.map(function (c) { return c.name; })))
            : branchPalette;
        data.children.forEach(function (child, i) {
            var childBranch = depth === 0 ? i : branchIndex;
            var childNode = buildOrgNode(app, node, child, 0, 0, depth + 1, childBranch, palette_1);
            if (collapsed)
                childNode.visible = false;
        });
    }
    // Prefer live tree count after children are attached (stays accurate if tree mutates)
    if (descendantCount > 0) {
        var live = countOrgDescendants(node);
        node.metadata.descendantCount = live;
        if (node.metadata.collapseButton) {
            updateOrgCollapseButton(node, collapsed);
        }
    }
    parent.add(node);
    return node;
}
function findOrgChartRoot(node) {
    var _a;
    var cur = node;
    while (cur) {
        if (((_a = cur.metadata) === null || _a === void 0 ? void 0 : _a.diagramType) === 'orgChart')
            return cur;
        cur = cur.parent;
    }
    return null;
}
/** Wire click handlers on minimize/expand buttons. */
export function wireOrgCollapseControls(app, root) {
    var walk = function (parent) {
        var e_2, _a;
        var _b, _c, _d, _e;
        var _loop_1 = function (child) {
            if (!('children' in child))
                return "continue";
            var g = child;
            if ((_b = g.metadata) === null || _b === void 0 ? void 0 : _b.orgNode) {
                var btn = (_c = g.metadata.collapseButton) !== null && _c !== void 0 ? _c : g.children.find(function (c) { var _a; return (_a = c.metadata) === null || _a === void 0 ? void 0 : _a.orgCollapseBtn; });
                if (btn && !((_d = btn.metadata) === null || _d === void 0 ? void 0 : _d.orgCollapseWired)) {
                    var onDown = function (e) {
                        e.stopPropagation();
                    };
                    var onClick = function (e) {
                        e.stopPropagation();
                        toggleOrgCollapse(g);
                        app.requestRender();
                    };
                    btn.on('mousedown', onDown);
                    btn.on('click', onClick);
                    btn.metadata.orgCollapseWired = true;
                    btn.listening = true;
                }
                // Keep collapse control above the editor hit target
                if (btn && btn.parent === g) {
                    g.remove(btn);
                    g.add(btn);
                }
            }
            if ((_e = g.children) === null || _e === void 0 ? void 0 : _e.length)
                walk(g);
        };
        try {
            for (var _f = __values(parent.children), _g = _f.next(); !_g.done; _g = _f.next()) {
                var child = _g.value;
                _loop_1(child);
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (_g && !_g.done && (_a = _f.return)) _a.call(_f);
            }
            finally { if (e_2) throw e_2.error; }
        }
    };
    walk(root);
}
/** Toggle org chart branch collapse — hide/show children, relayout, rewire. */
export function toggleOrgCollapse(node) {
    var e_3, _a, e_4, _b;
    var _c, _d, _e, _f, _g, _h, _j, _k, _l;
    if (!((_c = node.metadata) === null || _c === void 0 ? void 0 : _c.orgNode))
        return;
    var group = node;
    var collapsed = !node.metadata.collapsed;
    node.metadata.collapsed = collapsed;
    setDiagramState(node, { collapsed: collapsed });
    var children = group.children.filter(function (c) { var _a; return (_a = c.metadata) === null || _a === void 0 ? void 0 : _a.orgNode; });
    try {
        for (var children_1 = __values(children), children_1_1 = children_1.next(); !children_1_1.done; children_1_1 = children_1.next()) {
            var child = children_1_1.value;
            child.visible = !collapsed;
            child.markDirty();
        }
    }
    catch (e_3_1) { e_3 = { error: e_3_1 }; }
    finally {
        try {
            if (children_1_1 && !children_1_1.done && (_a = children_1.return)) _a.call(children_1);
        }
        finally { if (e_3) throw e_3.error; }
    }
    updateOrgCollapseButton(group, collapsed);
    var root = findOrgChartRoot(node);
    var app = node.getApp();
    if (root && app) {
        var layout = (_e = (_d = root.metadata) === null || _d === void 0 ? void 0 : _d.orgLayout) !== null && _e !== void 0 ? _e : {};
        var levelGap = (_f = layout.levelGap) !== null && _f !== void 0 ? _f : 120;
        var siblingGap = (_g = layout.siblingGap) !== null && _g !== void 0 ? _g : 28;
        try {
            for (var _m = __values(root.children.filter(function (c) { var _a; return (_a = c.metadata) === null || _a === void 0 ? void 0 : _a.orgNode; })), _o = _m.next(); !_o.done; _o = _m.next()) {
                var orgRoot = _o.value;
                layoutDiagram(orgRoot, levelGap, siblingGap);
            }
        }
        catch (e_4_1) { e_4 = { error: e_4_1 }; }
        finally {
            try {
                if (_o && !_o.done && (_b = _m.return)) _b.call(_m);
            }
            finally { if (e_4) throw e_4.error; }
        }
        wireOrgChartConnectors(app, root);
        wireOrgCollapseControls(app, root);
        var canvas = readCanvasSize((_j = (_h = root.metadata) === null || _h === void 0 ? void 0 : _h.diagramState) !== null && _j !== void 0 ? _j : {});
        if (canvas.width > 0 && canvas.height > 0) {
            fitDiagramToBounds(root, canvas.width, canvas.height, 24);
        }
        // Clear dotted selection chrome for nodes now hidden under a minimized branch
        var editor = (_k = root.metadata) === null || _k === void 0 ? void 0 : _k.diagramEditor;
        (_l = editor === null || editor === void 0 ? void 0 : editor.afterStructureChange) === null || _l === void 0 ? void 0 : _l.call(editor);
    }
    node.markDirty();
}
